import { GATHER_TIERS } from '../data/farm';
import { WIN_BALANCE, WIN_HENS } from '../data/balance';
import { farmTotals, nestBoxCost, roosterCost, roosterSlots } from './economy';
import { formatDuration, formatMoney } from './format';
import { countBoxes, countHens } from './state';
import type { GameState, SimCtx } from './types';

/**
 * The coach: one always-visible "next best action" line. Replaces v0.9's
 * toast prompts (players bounced from the default tab without a direction).
 */
export type GoalFocus = 'nests' | 'hens' | 'farm' | 'roosters' | 'upgrades' | 'goals';

export interface Goal {
  text: string;
  focus: GoalFocus;
  /** 0..1 progress toward this goal, when meaningful. */
  progress?: number;
}

export function nextGoal(s: GameState, ctx: SimCtx): Goal {
  const hens = countHens(s);
  const view = s.flocks.find((f) => f.id === s.viewFlock) ?? s.flocks[0]!;
  if (s.eggsCollected < 5) return { text: 'Click your hens to collect their eggs.', focus: 'nests' };
  const boxCost = nestBoxCost(s, view.id);
  if (countBoxes(s) < 3) {
    return s.money >= boxCost
      ? { text: `Buy a nesting box (${formatMoney(boxCost)}) in the Hens tab.`, focus: 'hens' }
      : { text: `Collect eggs to afford a nesting box (${formatMoney(boxCost)}).`, focus: 'nests', progress: s.money / boxCost };
  }
  if (view.boxes.some((b) => !b.hen)) return { text: 'Pick a breed for your empty nesting box.', focus: 'hens' };
  if (!Object.values(s.gather).some((n) => n > 0)) {
    const basket = GATHER_TIERS[0]!;
    return { text: `Buy a ${basket.name} (${formatMoney(basket.cost)}) so eggs get gathered without clicking.`, focus: 'farm', progress: Math.min(1, s.money / basket.cost) };
  }
  const slots = roosterSlots(s);
  if (slots.roosters === 0) {
    if (slots.open > 0) return { text: `Hire your first rooster (${formatMoney(roosterCost(s))}) in the Roosters tab.`, focus: 'roosters' };
    const need = slots.nextAtHens ?? 5;
    return { text: `Grow to ${need} hens to unlock your first rooster (${hens}/${need}).`, focus: 'hens', progress: hens / need };
  }
  if (!s.won) {
    if (hens < WIN_HENS) return { text: `Stage 1: fill out ${WIN_HENS} hens (${hens}/${WIN_HENS}), then bank $1,000,000.`, focus: 'hens', progress: hens / WIN_HENS };
    return { text: `Stage 1: bank ${formatMoney(WIN_BALANCE)} (${Math.floor((s.money / WIN_BALANCE) * 100)}%).`, focus: 'upgrades', progress: s.money / WIN_BALANCE };
  }
  if (slots.roosters < 10) {
    if (slots.open === 0 && slots.nextAtHens) {
      return { text: `Grow to ${slots.nextAtHens} hens to open rooster slot #${slots.roosters + 1} (${hens}/${slots.nextAtHens}).`, focus: 'hens', progress: hens / slots.nextAtHens };
    }
    const cost = roosterCost(s);
    const income = farmTotals(s, ctx, true).passiveIncome;
    const eta = income > 0 && s.money < cost ? ` — about ${formatDuration(((cost - s.money) / income) * 1000)} of gathering` : '';
    return { text: `Save for rooster #${slots.roosters + 1}: ${formatMoney(cost)}${eta}.`, focus: 'roosters', progress: Math.min(1, s.money / cost) };
  }
  return { text: 'Ten roosters strong. Chase the challenges in the Goals tab.', focus: 'goals' };
}
